import type * as React from 'react';
import { graphql } from 'react-apollo';

import gql from 'graphql-tag';
import { compose } from 'recompose';

import user from 'js/lib/user';

import withSessionsV2EnrollmentEnabled from 'bundles/course-sessions/utils/withSessionsV2EnrollmentEnabled';
import pandasScheduleRevampExperiments from 'bundles/epic/clients/pandasScheduleRevamp';

export const LearnerCourseSchedulesQuery = gql`
  query LearnerCourseSchedulesScheduleIncentives($id: String!) {
    LearnerCourseSchedulesV1 @naptime {
      get(id: $id) {
        elements {
          id
          scheduleTypeContent
        }
      }
    }
  }
`;

/* Epic feature flag - do not use in isolation
 * Schedule Incentives and Personalized Schedule are two features that were rolled out together to all learners
 * excluding degree and enterprise learners. Also we want to show schedule incentives when personalized schedule is enabled.
 * We won't roll them back in the foreseeable future, so we let them share one rollout epic
 */
const isEpicEnabled = (): boolean => pandasScheduleRevampExperiments.preview('isPersonalizedScheduleEnabled');

interface WithIsScheduleIncentivesEnabledProps extends Record<string, unknown> {
  courseId: string;
}

export type PropsFromScheduleIncentivesEnabled = {
  isScheduleIncentivesEnabled: boolean;
  isScheduleStrict: boolean;
};

const withIsScheduleIncentivesEnabled = <T extends WithIsScheduleIncentivesEnabledProps>(
  Component: React.ComponentType<T & PropsFromScheduleIncentivesEnabled>
): React.ComponentType<T> => {
  return compose<T & PropsFromScheduleIncentivesEnabled, T>(
    withSessionsV2EnrollmentEnabled(({ courseId }) => courseId, undefined, true),
    graphql(LearnerCourseSchedulesQuery, {
      options: ({ courseId }: { courseId: string }) => ({
        variables: {
          id: user.get().id + '~' + courseId,
        },
        ssr: false,
      }),
      props: ({ data, ownProps }: any) => {
        const isStrict =
          data?.LearnerCourseSchedulesV1?.get?.elements?.[0]?.scheduleTypeContent?.orgCourseraOndemandScheduleStrict;
        const isScheduleIncentivesEnabled = isEpicEnabled() && ownProps?.sessionsV2EnrollmentEnabled && !isStrict;
        const sessionsV2EnrollmentEnabled = ownProps?.sessionsV2EnrollmentEnabled;

        return {
          ...ownProps,
          isScheduleStrict: isStrict || !sessionsV2EnrollmentEnabled,
          isScheduleIncentivesEnabled,
        };
      },
    })
  )(Component);
};

export default withIsScheduleIncentivesEnabled;
